/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Link from '@mui/material/Link';

import {
  Group,
  InfoOutlined,
} from '@mui/icons-material';
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
} from '@mui/material';
import BarnUsersAutocomplete from '../../../../Autocompletes/BarnUsers';
import { getBarnUsersForAssignmentString } from '../../../../../utils/scheduleHelper';
import User from '../../../../User';

const ScheduleParticipant = ({
  schedule,
  event,
  assignment,
  setAssignment,
  type,
  setType,
  disabledForBeingInPast,
  name,
  setName,
}) => {
  const [editParticipants, setEditParticipants] = React.useState(false);

  React.useEffect(() => {
    setEditParticipants(false)
  }, [schedule, event])

  const barnUsersMap = useSelector((state) => state.barn.barnUsersMap);

  return (
    <ListItem
      dense
      divider
      sx={{ px: 0 }}
    >
      <ListItemIcon><Group /></ListItemIcon>
      <ListItemText>
        {
          !editParticipants
            ? (
              <Tooltip
                title={type === 'Invitation'
                  ? 'Remove any participants and cancel their calendar invites'
                  : 'Invite participants and send them a calendar invite for this task'}
              >
                <Grid container>
                  {
                    getBarnUsersForAssignmentString(assignment || '', barnUsersMap).map((bu) => (
                      <Grid item><User key={`task-assignment-${bu.id}`} user={bu} chip sx={{ mr: 0 }} /></Grid>
                    ))
                  }
                  <Grid item>
                    <Link
                      component="button"
                      variant="body2"
                      color="secondary"
                      underline="hover"
                      onClick={() => {
                        setAssignment(!assignment
                          ? ''
                          : assignment);
                        setEditParticipants(true)
                      }}
                    >
                      {(assignment && assignment.length > 0)
                        ? 'Modify Participant(s)'
                        : 'Add Participant(s)'}
                    </Link>
                  </Grid>
                </Grid>
              </Tooltip>
            )
            : (
              <Box>
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <BarnUsersAutocomplete
                      label="Participant(s)"
                      id="barn-users-select-autocomplete-schedule-assignment"
                      multiple
                      value={getBarnUsersForAssignmentString(assignment || '', barnUsersMap)}
                      onSelect={(e, newBarnUsers) => {
                        const newBarnUsersString = newBarnUsers.map((bu) => (bu.id
                          ? `@[${bu.userFullName}](${bu.auth0UserId || bu.email})`
                          : `@[${bu.userFullName || bu}](${null})`)).join('');
                        setAssignment(newBarnUsersString);

                        // const updatedWorkflows = workflows;
                        // updatedWorkflows[index].scheduleAssignment = newBarnUsersString;

                        // take selected barn users and map to assignment syntax @[NAME](AUTH0_USER_ID)
                        // setAssignment(updatedWorkflows.map((ww) => ww.scheduleAssignment || '').join(''));

                        // Now assign to the workflow
                        // setWorkflows(JSON.parse(JSON.stringify(updatedWorkflows))); // force react to refresh
                      }}
                      limitTags={(schedule && schedule.id)
                        ? 2
                        : 5}
                      disabled={disabledForBeingInPast}
                    />
                  </Box>
                  {
                    assignment && assignment.length > 0 && (
                      <Box sx={{ textAlign: 'right' }}>
                        <Button
                          size="small"
                          sx={{
                            fontSize: 11,
                            pt: 0,
                          }}
                          disabled={disabledForBeingInPast}
                          onClick={() => {
                            const names = getBarnUsersForAssignmentString(assignment || '', barnUsersMap)
                            if (name.indexOf(' - ') > 0) {
                              setName(`${name.substring(0, name.indexOf(' - '))} - ${names.map((bu) => bu.userFullName).join(' | ')}`)
                            } else {
                              setName(`${name} - ${names.map((bu) => bu.userFullName).join(' | ')}`)
                            }
                          }}
                        >Add name to title
                        </Button>
                      </Box>
                    )
                  }
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Tooltip
                    title="Send participants a calendar invite and track their accept/decline responses"
                  >
                    <FormControlLabel
                      label="Send Calendar Invitation"
                      sx={{
                        m: 0,
                        mb: 0,
                        mr: 1,
                        fontSize: '12px !important',
                        fontWeight: 400,
                        lineHeight: '14px !important',
                        color: '#9e9e9e',
                      }}
                      labelPlacement="start"
                      control={(
                        <Checkbox
                          size="small"
                          sx={{
                            m: 0,
                            ml: 0.5,
                            fontSize: '12px !important',
                            p: 0,
                          }}
                          disabled={disabledForBeingInPast}
                          checked={type === 'Invitation'}
                          onChange={(e) => {
                            setType(e.target.checked
                              ? 'Invitation'
                              : 'Default')
                          }}
                          inputProps={{ 'aria-label': 'controlled' }}
                        />
                      )}
                    />
                  </Tooltip>
                  <Tooltip
                    title="Remove any participants for this task"
                  >
                    <Link
                      component="button"
                      variant="body2"
                      color="secondary"
                      disabled={disabledForBeingInPast}
                      underline="hover"
                      onClick={() => {
                        setAssignment(null);
                        setEditParticipants(false)
                      }}
                    >
                      Remove Participants
                    </Link>
                  </Tooltip>
                </Box>
              </Box>
            )
        }
      </ListItemText>
      <Tooltip title="Participants are not assigned the task, but allow you to associate the task with individuals other than the task assigned users. For example, a client, rider, or someone else not part of your organization.">
        <InfoOutlined
          sx={{
            ml: 1,
            fontSize: 20,
          }}
          color="secondary"
        />
      </Tooltip>
    </ListItem>
  )
};

ScheduleParticipant.propTypes = {
  schedule: PropTypes.object,
  event: PropTypes.object,
  assignment: PropTypes.string,
  setAssignment: PropTypes.func,
  type: PropTypes.string.isRequired,
  setType: PropTypes.func,
  disabledForBeingInPast: PropTypes.bool,
  name: PropTypes.string.isRequired,
  setName: PropTypes.func.isRequired,
};

ScheduleParticipant.defaultProps = {
  schedule: null,
  event: null,
  assignment: '',
  setType: () => {},
  setAssignment: () => {},
  disabledForBeingInPast: false,
};

export default ScheduleParticipant;
